<template>
  <AppDialogContent
    v-model="dialog"
    :error="formError"
    :persistent="formSubmitted"
    :loading="
      $apollo.queries.initPersonDebtBalanceForm.loading || formSubmitted
    "
    data-test="personFunctionForm"
    @submit-form="handleSubmit()"
  >
    <template #header>{{
      $t(
        "people:ShoppingBasketPage.debtsTab.addEditDebtBalance.modal.heading",
        "Add / edit debt balance"
      )
    }}</template>

    <p>
      {{
        $t(
          "people:ShoppingBasketPage.debtsTab.addEditDebtBalance.modal.hint",
          "When debt balance is recorded and is higher than 0, user cannot be entered to competitions until debts are cleared. For manually recorded debts, do not forget to remove debt after user has paid all overdue payment to organisation. For debts in shopping basket, all will be settled automatically after payment is confirmed."
        )
      }}
    </p>

    <AppSelectOrganisation
      v-if="v$.form.organisationId"
      v-model="form.organisationId"
      :label="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.debtToOrganisation.label',
          'Debt to organisation'
        )
      "
      :error-messages="validationErrors('organisationId')"
      :disabled="appFormMixinIsFieldDisabled('organisationId')"
      @blur="v$.form.organisationId.$touch()"
    />

    <AppMoneyField
      v-if="v$.form.balanceCents"
      v-model="form.balanceCents"
      :label="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.totalDebtAmount.label',
          'Total debt amount to organisation'
        )
      "
      :hint="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.totalDebtAmount.hint',
          'This amount represents total balance of debt to organisation. To record correctly, sum up all payables to get the correct amount. Only one balance allowed for one organisation.'
        )
      "
      persistent-hint
      outlined
      :error-messages="validationErrors('balanceCents')"
      :disabled="appFormMixinIsFieldDisabled('balanceCents')"
      data-test="balanceCents"
      @blur="v$.form.balanceCents.$touch()"
    />

    <v-textarea
      v-if="v$.form.noteToPerson"
      v-model="form.noteToPerson"
      :label="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.noteToUser.label',
          'Note to user regarding debt'
        )
      "
      :hint="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.noteToUser.hint',
          'Leave comment or instruction to user on how to pay his/her debts, for example, reference to invoice..'
        )
      "
      outlined
      :error-messages="validationErrors('noteToPerson')"
      :disabled="appFormMixinIsFieldDisabled('noteToPerson')"
      data-test="noteToPerson"
      @blur="v$.form.noteToPerson.$touch()"
    />

    <v-textarea
      v-if="v$.form.internalNote"
      v-model="form.internalNote"
      :label="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.internalNote.label',
          'Internal note regarding debt'
        )
      "
      :hint="
        $t(
          'people:ShoppingBasketPage.debtsTab.addEditDebtBalance.internalNote.hint',
          'Note will be only visible to administrators.'
        )
      "
      outlined
      :error-messages="validationErrors('internalNote')"
      :disabled="appFormMixinIsFieldDisabled('internalNote')"
      data-test="internalNote"
    />
  </AppDialogContent>
</template>
<script>
import { defineComponent } from "vue";
import gql from "fraql";

import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectOrganisation from "@AppSelects/AppSelectOrganisation.vue";
import AppMoneyField from "@elements/AppMoneyField.vue";
import appFormMixin from "@/mixins/appFormMixin";
import saveMeta from "@/mixins/save_meta";

import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";

export default defineComponent({
  name: "OrderDebtForm",
  components: {
    AppMoneyField,
    AppSelectOrganisation,
    AppDialogContent
  },
  mixins: [appFormMixin, saveMeta],
  setup() {
    const { v$ } = appFormMixin.setup();
    return {
      v$
    };
  },
  data() {
    return {
      form: {},
      showModal: false,
      formError: null,
      formSubmitted: false,
      initPersonDebtBalanceForm: {
        formFields: [],
        vuelidateValidations: {},
        readOnlyFields: []
      },
      appFormMixinValidationsConfVariable:
        "initPersonDebtBalanceForm.vuelidateValidations",
      appFormMixinReadOnlyFieldsVariable:
        "initPersonDebtBalanceForm.readOnlyFields"
    };
  },
  computed: {
    dialog: {
      get() {
        return this.showModal;
      },
      set(v) {
        if (v === false) {
          this.saveMetaUnsaveConfirm().then(response => {
            if (response) {
              this.showModal = false;
              this.form = {};
              this.saveMetaSetOriginalForm(this.form);
              return;
            }
            this.showModal = true;
          });
        }
        this.showModal = v;
      }
    }
  },
  apollo: {
    initPersonDebtBalanceForm: {
      query: gql`
        query initPersonDebtBalanceForm(
          $id: ID
          $organisationId: ID
          $personId: ID
        ) {
          initPersonDebtBalanceForm(
            id: $id
            organisationId: $organisationId
            personId: $personId
          ) {
            vuelidateValidations
            readOnlyFields
          }
        }
      `,
      skip() {
        return this.dialog == false;
      },
      variables() {
        return {
          id: this.form.id,
          organisationId: this.form.organisationId,
          personId: this.form.personId
        };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        this.initPersonDebtBalanceForm = data.initPersonDebtBalanceForm;
      }
    }
  },
  fragments: {
    personDebtBalanceFields: gql`
      fragment _ on PersonDebtBalance {
        personId
        organisationId
        noteToPerson
        internalNote
        balanceCents
      }
    `
  },
  methods: {
    validationErrors(fieldName) {
      return this.appFormMixinValidationErrorsForField(fieldName);
    },
    openForm(item) {
      if (item == null) item = {};

      this.form = {
        id: item.id ?? null,
        personId: item.personId,

        organisationId: item.organisationId ?? null,
        balanceCents: item.balanceCents ?? null,
        noteToPerson: item.noteToPerson ?? null,
        internalNote: item.internalNote ?? null
      };

      this.saveMetaSetOriginalForm(this.form);
      this.v$.form.$reset();
      this.formError = {};
      this.showModal = true;
    },
    handleSubmit() {
      this.formError = {};
      this.v$.form.$touch();
      if (this.v$.form.$invalid) return false;
      this.formSubmitted = true;

      this.create();
    },
    async create() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation personDebtBalanceCreateOrUpdate(
              $id: ID
              $personId: ID
              $organisationId: ID
              $balanceCents: Int
              $noteToPerson: String
              $internalNote: String
            ) {
              personDebtBalanceCreateOrUpdate(
                input: {
                  id: $id
                  personId: $personId
                  organisationId: $organisationId
                  balanceCents: $balanceCents
                  noteToPerson: $noteToPerson
                  internalNote: $internalNote
                }
              ) {
                personDebtBalance {
                  id
                  organisationId
                  organisation {
                    id 
                    ${AppShowOrganisationName.fragments.organisation}
                  }
                  dataSourceType
                  updatedAt
                  noteToPerson
                  balanceCents
                  internalNote
                  allowedMutations
                }
              }
            }
          `,
          variables: {
            id: this.form.id,
            personId: this.form.personId,
            balanceCents: this.form.balanceCents,
            organisationId: this.form.organisationId,
            noteToPerson: this.form.noteToPerson,
            internalNote: this.form.internalNote
          }
        });

        this.saveMetaSetOriginalForm(this.form);
        this.$emit("refetch");
        this.dialog = false;
        this.$root.$snackbar("success");
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.formSubmitted = false;
      }
    }
  }
});
</script>
